import {
  Component,
  OnInit,
  signal,
  PendingTasks,
  inject,
  input,
  TransferState,
  PLATFORM_ID,
} from '@angular/core';
import { NodeGeneralAgency, NodeLocation } from '@app/models/entity.model';
import { EntitySharedModule } from '@app/modules/entity.shared.module';
import { TextsService } from '@app/services/texts.service';
import { ParagraphPgPnav } from '@app/models/paragraph.model';
import { BreakpointService } from '@app/services/breakpoint.service';
import { DOCUMENT } from '@angular/common';
import { HeaderComponent } from '@app/entities/node/node-general-agency/header/header.component';
import { AgencyInfoComponent } from './agency-info/agency-info.component';
import { PersonViewComponent } from './person-view/person-view.component';
import { AgencyMapsComponent } from './agency-maps/agency-maps.component';
import { AgencyNewsComponent } from './agency-news/agency-news.component';
import { AgencyPromotedContentComponent } from './agency-promoted-content/agency-promoted-content.component';
import { DataService } from '@app/services/data.service';
import { AgencyInfoNewComponent } from '@app/entities/node/node-general-agency/agency-info-new/agency-info-new.component';
import { ContentSectionComponent } from '@app/components/content-section/content-section.component';
import { TextsPipe } from '@app/pipes/texts.pipe';
import { AnchorFormatterPipe } from '@app/pipes/anchor-formatter.pipe';

@Component({
  selector: 'mobi-node-general-agency',
  templateUrl: './node-general-agency.component.html',
  styleUrls: ['./node-general-agency.component.scss'],
  standalone: true,
  imports: [
    EntitySharedModule,
    HeaderComponent,
    AgencyInfoComponent,
    AgencyInfoNewComponent,
    PersonViewComponent,
    AgencyMapsComponent,
    AgencyNewsComponent,
    AgencyPromotedContentComponent,
    ContentSectionComponent,
    TextsPipe,
    AnchorFormatterPipe,
  ],
})
export class NodeGeneralAgencyComponent implements OnInit {
  readonly entity = input.required<NodeGeneralAgency>();
  mainAgency = signal<NodeLocation | undefined>(undefined);
  openingHours = signal('');
  locations = signal<NodeLocation[] | undefined>([]);
  locationItems = signal<string[] | undefined>([]);
  contentLinks = signal<ParagraphPgPnav[]>([]);
  #data = inject(DataService);
  #texts = inject(TextsService);
  breakpoint = inject(BreakpointService);
  #document = inject(DOCUMENT);
  #platformId = inject(PLATFORM_ID);
  #task = inject(PendingTasks);
  #state = inject(TransferState);

  async ngOnInit() {
    const cleanup = this.#task.add();
    try {
      await this.getCurrentOpeningHours();

      let locations = await this.#data.getEntities<NodeLocation>('locationsQuery', {
        id: this.entity().id,
      });
      if (locations.length) {
        this.locations.set(locations);

        locations = locations?.filter(Boolean).sort(this.#sortLocations);

        this.locationItems.set(
          locations.filter((item) => !item !== null).map((item) => item?.title || '')
        );

        const agency: NodeLocation | undefined = locations.find(
          (location: NodeLocation) => location.mainAgency
        );

        if (agency && this.entity().gaWap) {
          this.mainAgency.set({ ...agency, phoneWhatsapp: agency.phone });
        } else {
          this.mainAgency.set(agency);
        }
      }

      await this.getContentLinks();
      cleanup();
    } catch (e) {
      console.log(e);
      cleanup();
    }
  }

  async getContentLinks() {
    if (this.mainAgency()?.phone) {
      const servicesTexts = await Promise.all([
        this.#texts.getText('ga_services_content_link_name', 'frontend', 'Dienste'),
        this.#texts.getText(
          'ga_services_content_link_paramenter_name',
          'frontend',
          'dienste'
        ),
      ]);
      this.contentLinks.update((values) => [
        ...values,
        {
          label: servicesTexts[0],
          sectionId: servicesTexts[1],
        },
      ]);
    }
    const personViewTexts = await Promise.all([
      this.#texts.getText('ga_person_view_name', 'frontend', 'Unser Team'),
      this.#texts.getText('ga_person_view_parameter_name', 'frontend', 'unser-team'),
    ]);
    this.contentLinks.update((values) => [
      ...values,
      {
        label: personViewTexts[0],
        sectionId: personViewTexts[1],
      },
    ]);

    if (this.locations() && this.locations()!.length) {
      const locationTexts = await Promise.all([
        this.#texts.getText('locations_content_link_name', 'frontend', 'Standorte'),
        this.#texts.getText(
          'locations_content_link_paramenter_name',
          'frontend',
          'standorte'
        ),
      ]);

      this.contentLinks.update((values) => [
        ...values,
        {
          label: locationTexts[0],
          sectionId: locationTexts[1],
        },
      ]);
    }
    const newsViewTexts = await Promise.all([
      this.#texts.getText('ga_news_view_name', 'frontend', 'Aus der Region'),
      this.#texts.getText('a_news_view_paramenter_name', 'frontend', 'aus-der-region'),
    ]);

    this.contentLinks.update((values) => [
      ...values,
      {
        label: newsViewTexts[0],
        sectionId: newsViewTexts[1],
      },
    ]);

    const recommendedViewTexts = await Promise.all([
      this.#texts.getText('ga_recommended_view_name', 'frontend', 'Empfohlene Inhalte'),
      this.#texts.getText(
        'ga_recommended_view_paramenter_name',
        'frontend',
        'empfohlene-inhalte'
      ),
    ]);

    this.contentLinks.update((values) => [
      ...values,
      {
        label: recommendedViewTexts[0],
        sectionId: recommendedViewTexts[1],
      },
    ]);
  }
  telnrAsLink(telnr: string): string {
    return 'tel:' + telnr.replaceAll(' ', '');
  }

  async getCurrentOpeningHours() {
    const currentTime = new Date();
    if (this.entity().openingHoursOverwrite) {
      let valid = true;

      if (this.entity().openingHoursOverwriteStart?.value) {
        const start = new Date(this.entity().openingHoursOverwriteStart!.value + 'Z');
        if (currentTime.getTime() < start.getTime()) {
          valid = false;
        }
      }

      if (this.entity().openingHoursOverwriteEnd?.value) {
        const end = new Date(this.entity().openingHoursOverwriteEnd!.value + 'Z');
        if (currentTime.getTime() > end.getTime()) {
          valid = false;
        }
      }

      if (valid) {
        this.openingHours.set(this.entity().openingHoursOverwrite ?? '');

        return;
      }
    }

    try {
      const data = await this.#texts.getText(
        'ga_closed',
        'frontend',
        'Heute geschlossen'
      );
      const currentDay = currentTime.getDay();
      const currentlyClosed = data;
      switch (currentDay) {
        case 0:
          this.openingHours.set(this.entity().openingHoursSun || '');
          break;
        case 1:
          this.openingHours.set(this.entity().openingHoursMon || '');
          break;
        case 2:
          this.openingHours.set(this.entity().openingHoursTue || '');
          break;
        case 3:
          this.openingHours.set(this.entity().openingHoursWed || '');
          break;
        case 4:
          this.openingHours.set(this.entity().openingHoursThu || '');
          break;
        case 5:
          this.openingHours.set(this.entity().openingHoursFri || '');
          break;
        case 6:
          this.openingHours.set(this.entity().openingHoursSat || '');
          break;
        default:
          this.openingHours.set(currentlyClosed || '');
          break;
      }
      if (!this.openingHours()) {
        this.openingHours.set(currentlyClosed);
      }
    } catch (e) {
      this.#handleError(e);
    }
  }

  #handleError(e: any) {
    console.error(e);
    this.#document?.dispatchEvent(
      new CustomEvent('tracking:system', {
        detail: {
          message:
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`,
          type: 'error',
        },
      })
    );
  }

  #sortLocations(a: NodeLocation, b: NodeLocation) {
    if (a.mainAgency && !b.mainAgency) {
      return -1;
    } else if (!a.mainAgency && b.mainAgency) {
      return 1;
    } else {
      return a.title.localeCompare(b.title);
    }
  }
}
